// ═══════════════════════════════════════════════════════════════
// Impact — Module Layout with grouped sub-navigation
// ═══════════════════════════════════════════════════════════════

import { Outlet, useLocation } from 'react-router-dom';
import { Group, Tooltip, CopyButton, ActionIcon, Button } from '../../components/gab/GabComponents';
import {
  IconLayoutDashboard, IconList, IconBolt, IconClipboardCheck,
  IconMap, IconChartBar, IconFileExport, IconStack2, IconWorld, IconCheck, IconCopy,
  IconInbox, IconMicrophone,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import type { NavItem } from '../../types/nav';
import ModuleNav from '../../components/layout/ModuleNav';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { brand } from '../../lib/colors';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',    path: '/impact',               icon: IconLayoutDashboard, exact: true },
  { label: 'Assessments',  path: '/impact/assessments',   icon: IconList },
  { label: 'VIDA',         path: '/impact/vida',          icon: IconMicrophone, minLevel: 'editor' },
  { label: 'Quick Capture',path: '/impact/capture',       icon: IconBolt,           minLevel: 'editor' },
  { label: 'Detailed',     path: '/impact/detailed',      icon: IconClipboardCheck, minLevel: 'editor' },
  { label: 'Bulk Entry',   path: '/impact/bulk',          icon: IconStack2,         minLevel: 'editor' },
  { label: 'Map',          path: '/impact/map',           icon: IconMap },
  { label: 'Analytics',    path: '/impact/analytics',     icon: IconChartBar },
  { label: 'Reports',      path: '/impact/reports',       icon: IconFileExport },
  { label: 'Self-Reports', path: '/impact/self-reports',  icon: IconInbox,          minLevel: 'editor' },
];

const NAV_GROUPS = [
  { label: '',        items: NAV_ITEMS.filter(i => ['/impact', '/impact/assessments'].includes(i.path)) },
  { label: 'Capture', items: NAV_ITEMS.filter(i => ['/impact/vida', '/impact/capture', '/impact/detailed', '/impact/bulk'].includes(i.path)) },
  { label: 'Reports', items: NAV_ITEMS.filter(i => ['/impact/map', '/impact/analytics', '/impact/reports', '/impact/self-reports'].includes(i.path)) },
];

export default function ImpactLayout() {
  const { pathname } = useLocation();
  const { activeIncident } = useAuthStore();
  const visibleItems = useFilteredNav('impact', NAV_ITEMS);

  const isDetail = pathname.startsWith('/impact/assessment/');

  const selfReportUrl = activeIncident
    ? `${window.location.origin}/self-report/${activeIncident.id}`
    : '';

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  // Build visible groups (filter each group's items through RBAC)
  const visiblePaths = new Set(visibleItems.map(i => i.path));
  const visibleGroups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(i => visiblePaths.has(i.path)),
  })).filter(g => g.items.length > 0);

  const extra = activeIncident ? (
    <Group gap={4}>
      <Tooltip label="Open public damage report portal" withArrow>
        <Button
          variant="light"
          color="orange"
          size="xs"
          leftSection={<IconWorld size={14} />}
          component="a"
          href={selfReportUrl}
          target="_blank"
        >
          Public Report
        </Button>
      </Tooltip>
      <CopyButton value={selfReportUrl}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied!' : 'Copy link'} withArrow>
            <ActionIcon variant="light" color={copied ? 'green' : 'orange'} size="sm" onClick={copy}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  ) : undefined;

  return (
    <div style={{ padding: '24px' }}>
      {!isDetail && (
        <>
          <Breadcrumbs
            items={[
              { label: 'Hub', to: '/' },
              { label: 'Impact', to: '/impact' },
              ...(currentPage && currentPage.path !== '/impact' ? [{ label: currentPage.label }] : []),
            ]}
          />
          <ModuleNav
            items={visibleItems}
            groups={visibleGroups}
            accentColor={brand.blurple}
            extra={extra}
          />
        </>
      )}
      <Outlet />
    </div>
  );
}
